<script setup lang="ts">
  import { useLocale } from '@tundr/i18n';
  import { computed, useId } from 'vue';
  import Checkbox from '../Checkbox/Checkbox.vue';
  import {
    itemsClass,
    rootClass,
    titleClass,
    visuallyHiddenClass,
  } from './CheckboxGroup.css';
  import type {
    CheckboxGroupEmits,
    CheckboxGroupOption,
    CheckboxGroupProps,
  } from './CheckboxGroup.types';

  const { t } = useLocale('ds-components');

  const props = withDefaults(defineProps<CheckboxGroupProps>(), {
    showSelectAll: true,
  });

  const emit = defineEmits<CheckboxGroupEmits>();

  const autoId = useId();
  const groupId = computed(() => props.id ?? autoId);
  const titleId = computed(() => `${groupId.value}-title`);
  const resolvedTestId = computed(
    () => props.testId ?? `ds-checkbox-group-${autoId}`,
  );

  const selectedCount = computed(
    () => props.options.filter((option) => option.selected).length,
  );
  const allSelected = computed(
    () =>
      props.options.length > 0 && selectedCount.value === props.options.length,
  );
  const someSelected = computed(
    () => selectedCount.value > 0 && !allSelected.value,
  );

  const toggleAll = (selected: boolean) => {
    const updatedOptions: CheckboxGroupOption[] = props.options.map(
      (option) => ({ ...option, selected }),
    );
    emit('update:options', updatedOptions);
  };

  const updateOption = (index: number, selected: boolean) => {
    const updatedOptions = [...props.options];
    updatedOptions[index] = { ...updatedOptions[index], selected };
    emit('update:options', updatedOptions);
  };
</script>

<template>
  <div
    :class="rootClass"
    role="group"
    :aria-labelledby="titleId"
    :data-testid="resolvedTestId"
  >
    <span :id="titleId" :class="[titleClass, !title && visuallyHiddenClass]">
      {{ title || t('checkbox_group.default_group_label') }}
    </span>
    <Checkbox
      v-if="showSelectAll"
      :model-value="allSelected"
      :indeterminate="someSelected"
      :label="t('checkbox_group.select_all_label')"
      :name="`${groupId}-all`"
      @update:model-value="toggleAll"
    />
    <div :class="itemsClass">
      <Checkbox
        v-for="(option, index) in options"
        :key="option.value"
        :model-value="option.selected"
        :label="option.label"
        :name="`${groupId}-${option.value}`"
        :value="option.value"
        @update:model-value="
          (selected: boolean) => updateOption(index, selected)
        "
      />
    </div>
  </div>
</template>
